import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, PieChart, Newspaper } from 'lucide-react';

const Investments = () => {
  const [portfolioValue, setPortfolioValue] = useState(45678.90);
  const [portfolioChange, setPortfolioChange] = useState(3.2);
  
  const [stocks] = useState([
    {
      symbol: 'AAPL',
      name: 'Apple Inc.',
      shares: 10,
      avgPrice: 150.00,
      currentPrice: 175.23,
      change: 2.5,
      chartData: [
        { time: '9:30', price: 170 },
        { time: '10:30', price: 172 },
        { time: '11:30', price: 173 },
        { time: '12:30', price: 171 },
        { time: '13:30', price: 174 },
        { time: '14:30', price: 175.23 },
      ]
    },
    {
      symbol: 'TSLA',
      name: 'Tesla, Inc.',
      shares: 5,
      avgPrice: 200.00,
      currentPrice: 242.15,
      change: -1.2,
      chartData: [
        { time: '9:30', price: 245 },
        { time: '10:30', price: 244 },
        { time: '11:30', price: 243 },
        { time: '12:30', price: 241 },
        { time: '13:30', price: 242 },
        { time: '14:30', price: 242.15 },
      ]
    }
  ]);

  const [news] = useState([
    {
      id: 1,
      title: 'Fed Signals Potential Rate Cuts',
      source: 'Financial Times',
      time: '2h ago',
      impact: 'positive'
    },
    {
      id: 2,
      title: 'Tech Sector Shows Strong Growth',
      source: 'Bloomberg',
      time: '4h ago',
      impact: 'positive'
    }
  ]);

  return (
    <div className="space-y-6">
      {/* Portfolio Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">
              Portfolio Value
            </h2>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-3xl font-bold text-[#68A511]">
                ${portfolioValue.toLocaleString()}
              </span>
              <div className={`flex items-center ${portfolioChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {portfolioChange >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                <span className="ml-1">{Math.abs(portfolioChange)}%</span>
              </div>
            </div>
          </div>
          <PieChart className="text-[#68A511] w-12 h-12" />
        </div>

        {/* Risk Assessment */}
        <div className="p-4 bg-[#DCE9C2] rounded-lg mt-4">
          <p className="text-[#598615] flex items-center gap-2">
            <AlertCircle size={20} />
            Portfolio Risk: Moderate | Diversification Score: 8.5/10
          </p>
        </div>
      </motion.div>

      {/* Stock Holdings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        {stocks.map((stock) => (
          <div
            key={stock.symbol}
            className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200">
                  {stock.symbol}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">{stock.name}</p>
              </div>
              <div className={`flex items-center ${stock.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {stock.change >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                <span className="ml-1">{Math.abs(stock.change)}%</span>
              </div>
            </div>

            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stock.chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DCE9C2" />
                  <XAxis dataKey="time" stroke="#598615" />
                  <YAxis stroke="#598615" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '8px',
                      border: 'none',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#68A511"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="p-3 bg-[#DCE9C2] rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Shares</p>
                <p className="text-lg font-semibold text-[#598615]">{stock.shares}</p>
              </div>
              <div className="p-3 bg-[#DCE9C2] rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Price</p>
                <p className="text-lg font-semibold text-[#598615]">${stock.avgPrice}</p>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Market News */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <div className="flex items-center gap-3 mb-6">
          <Newspaper className="text-[#68A511]" size={24} />
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">
            Market News
          </h2>
        </div>
        <div className="space-y-4">
          {news.map((item) => (
            <div
              key={item.id}
              className="p-4 bg-[#DCE9C2] rounded-lg flex items-center justify-between"
            >
              <div>
                <h4 className="font-medium text-[#598615]">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.source} • {item.time}</p>
              </div>
              <div className={`px-3 py-1 rounded-full ${
                item.impact === 'positive' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              }`}>
                {item.impact}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Investments; 